import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import { apiFetch } from '@/lib/api';
import { useRootLabel, useScopeStore, findNodeInTree } from '@/stores/scope';
import { useScopePinStore } from '@/stores/scope-pin';
import { MonthPicker } from '@/components/MonthPicker';
import { formatCurrency, formatCount, formatPercent } from '@/lib/format';

type TrendRow = {
  period: string;
  period_label: string;
  store_sales_tax_excluded: number;
  delivery_sales_tax_excluded: number;
  total_sales_tax_excluded: number;
  cogs_tax_excluded: number;
  labor_cost: number;
  opex: number;
  customer_count: number;
  avg_price_tax_excluded: number | null;
  cost_rate: number | null;
  gross_margin_rate: number | null;
  labor_rate: number | null;
  opex_rate: number | null;
  operating_profit_rate: number | null;
  publish_gate?: { excluded_store_ids: string[]; partial_month: boolean };
};

type TrendsResponse = {
  period: { start: string; end: string };
  store_id: string | null;
  series: TrendRow[];
};

const BRAND = {
  red: '#C91F28',
  orange: '#F05A28',
  gold: '#F2B705',
  black: '#1A1A1A',
  gray: '#8D8D8D',
  blue: '#6E80A8',
};

/** N ヶ月前の月初日 */
const monthsBackStart = (n: number): string => {
  const d = new Date();
  d.setDate(1);
  d.setMonth(d.getMonth() - n + 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
};

const currentMonthStart = (): string => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
};

const presets = [
  { label: '直近3ヶ月', months: 3 },
  { label: '直近6ヶ月', months: 6 },
  { label: '直近12ヶ月', months: 12 },
  { label: '直近24ヶ月', months: 24 },
];

const RATE_METRICS: Array<{ key: keyof TrendRow; label: string; color: string }> = [
  { key: 'cost_rate',             label: '原価率',     color: BRAND.orange },
  { key: 'gross_margin_rate',     label: '粗利率',     color: BRAND.red },
  { key: 'labor_rate',            label: '人件費率',   color: BRAND.blue },
  { key: 'opex_rate',             label: '諸経費率',   color: BRAND.gold },
  { key: 'operating_profit_rate', label: '営業利益率', color: BRAND.black },
];

export function TrendsPage() {
  const rootLabel = useRootLabel();
  // 表示スコープは NodeSwitcher (useScopeStore.selectedNodeId) を参照する (T-1003、
  // docs/design/v025-config-ui.md Design-C2 対応)。旧実装の「対象」<select> (storeId ローカル state)
  // と ?store_id= クエリパラメータの後方互換は廃止 — スコープ選択は NodeSwitcher に一本化する。
  const scopeRoots = useScopeStore((s) => s.roots);
  const selectedNodeId = useScopeStore((s) => s.selectedNodeId);
  const selectedNode = selectedNodeId ? findNodeInTree(scopeRoots, selectedNodeId) : null;
  const scopeLabel = selectedNode ? selectedNode.name : `${rootLabel}（合算）`;

  // 5-1 (docs/design/v05-dashboard-scope-pinning.md §0-2-4): 固定中は開始月・終了月ペアをそのまま採用
  const isPinned = useScopePinStore((s) => s.isPinned);
  const pinnedPeriodStart = useScopePinStore((s) => s.pinnedPeriodStart);
  const pinnedPeriodEnd = useScopePinStore((s) => s.pinnedPeriodEnd);
  const [periodStart, setPeriodStart] = useState(() => (isPinned && pinnedPeriodStart ? pinnedPeriodStart : monthsBackStart(12)));
  const [periodEnd, setPeriodEnd] = useState(() => (isPinned && pinnedPeriodEnd ? pinnedPeriodEnd : currentMonthStart()));
  // §0-2-2: storeの復元が初回マウントより後に完了する場合の補正。プリセットボタン操作等の
  // ページ内変更ではisPinned/pinned*が変わらないため、この effect が上書きすることはない。
  useEffect(() => {
    if (isPinned && pinnedPeriodStart && pinnedPeriodEnd) {
      setPeriodStart(pinnedPeriodStart);
      setPeriodEnd(pinnedPeriodEnd);
    }
  }, [isPinned, pinnedPeriodStart, pinnedPeriodEnd]);
  const [enabledMetrics, setEnabledMetrics] = useState<Set<string>>(
    () => new Set(RATE_METRICS.map((m) => m.key as string)),
  );
  const toggleMetric = (key: string) => setEnabledMetrics((prev) => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  const { data, isLoading, isError, error } = useQuery<TrendsResponse>({
    queryKey: ['dashboard-trends', periodStart, periodEnd, selectedNodeId],
    queryFn: () => apiFetch<TrendsResponse>(
      `/dashboard/trends?period_start=${periodStart}&period_end=${periodEnd}` +
      (selectedNodeId ? `&store_id=${selectedNodeId}` : ''),
    ),
  });

  const series = data?.series ?? [];

  // v0.3 (S-9データ公開ゲート、R-S9-02): 月レンジの中で「未公開店舗を含む月」が3ヶ月以上ある場合のみ
  // バナー表示 (1-2ヶ月程度の一時的な未公開は毎回警告すると煩雑なため)
  const partialMonthCount = series.filter((r) => r.publish_gate?.partial_month).length;
  const showPartialBanner = partialMonthCount >= 3;

  // 累計サマリ
  const summary = useMemo(() => {
    if (series.length === 0) return null;
    const totalSales = series.reduce((s, r) => s + r.total_sales_tax_excluded, 0);
    const totalCogs = series.reduce((s, r) => s + r.cogs_tax_excluded, 0);
    const totalCustomers = series.reduce((s, r) => s + r.customer_count, 0);
    const storeSalesSum = series.reduce((s, r) => s + r.store_sales_tax_excluded, 0);
    return {
      totalSales, totalCogs, totalCustomers, storeSalesSum,
      avgPrice: totalCustomers > 0 ? Math.round(storeSalesSum / totalCustomers) : null,
      costRate: totalSales > 0 ? totalCogs / totalSales : null,
    };
  }, [series]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h1 className="text-h1">KPI推移</h1>
      </div>

      {showPartialBanner && (
        <div className="rounded-lg bg-alert-bg border border-alert-border px-4 py-3 text-[13px] text-alert-text" role="note">
          対象期間中 {partialMonthCount}ヶ月分で未公開の店舗があり、集計から除外されています
        </div>
      )}

      {/* フィルタバー */}
      <div className="border border-neutral-line rounded-lg bg-white p-4 space-y-3">
        <div className="flex items-end gap-3 flex-wrap">
          <div>
            <label className="block text-xs text-neutral-sub mb-1">対象</label>
            <div className="min-h-[36px] px-3 py-1.5 rounded border border-neutral-line bg-neutral-panel text-sm text-neutral-text flex items-center">
              {scopeLabel}
            </div>
          </div>
          <div>
            <label className="block text-xs text-neutral-sub mb-1">期間開始</label>
            <MonthPicker aria-label="期間開始" value={periodStart} onChange={(v) => setPeriodStart(v + '-01')}
              className="px-3 py-1.5 rounded border border-neutral-line text-sm" />
          </div>
          <div>
            <label className="block text-xs text-neutral-sub mb-1">期間終了</label>
            <MonthPicker aria-label="期間終了" align="right" value={periodEnd} onChange={(v) => setPeriodEnd(v + '-01')}
              className="px-3 py-1.5 rounded border border-neutral-line text-sm" />
          </div>
          <div className="flex gap-1 flex-wrap">
            {presets.map((p) => (
              <button key={p.label}
                onClick={() => { setPeriodStart(monthsBackStart(p.months)); setPeriodEnd(currentMonthStart()); }}
                className="min-h-[36px] px-3 py-1.5 text-xs rounded border border-neutral-line hover:bg-neutral-panel">
                {p.label}
              </button>
            ))}
          </div>
        </div>
        <p className="text-xs text-neutral-sub">対象スコープは画面上部の切替メニューで変更できます</p>
      </div>

      {isLoading && <div className="text-neutral-sub">読み込み中…</div>}
      {isError && <div className="p-3 rounded bg-warning-bg text-warning-text border border-warning-border">{(error as Error).message}</div>}

      {!isLoading && !isError && series.length === 0 && (
        <div className="p-6 text-center text-neutral-sub">対象期間のデータがありません</div>
      )}

      {series.length > 0 && (
        <>
          {/* サマリカード */}
          {summary && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <SummaryTile label="期間合計売上（税抜）" value={formatCurrency(summary.totalSales)} />
              <SummaryTile label="期間合計原価（税抜）" value={formatCurrency(summary.totalCogs)} />
              <SummaryTile label="期間合計来客数" value={formatCount(summary.totalCustomers, ' 人')} />
              <SummaryTile label="期間平均原価率" value={formatPercent(summary.costRate)} />
            </div>
          )}

          {/* 売上高：積み上げ棒グラフ（店舗内 + デリバリー） */}
          <ChartCard title="売上高（税抜） 推移">
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={series} margin={{ top: 10, right: 12, left: 0, bottom: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E5E5" />
                <XAxis dataKey="period_label" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => (v / 1_000_000).toFixed(0) + '百万'} />
                <Tooltip
                  formatter={(v: number, k: string) => [formatCurrency(v), labelJa(k)]}
                  labelFormatter={(l) => l as string}
                />
                <Legend formatter={(v) => labelJa(v)} />
                <Bar dataKey="store_sales_tax_excluded" name="store_sales_tax_excluded" stackId="a" fill={BRAND.red} />
                <Bar dataKey="delivery_sales_tax_excluded" name="delivery_sales_tax_excluded" stackId="a" fill={BRAND.orange} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* 来客数 */}
            <ChartCard title="来客数 推移">
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={series} margin={{ top: 10, right: 12, left: 0, bottom: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E5E5" />
                  <XAxis dataKey="period_label" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => (v / 1000).toFixed(1) + '千'} />
                  <Tooltip formatter={(v: number) => formatCount(v, ' 人')} />
                  <Bar dataKey="customer_count" name="customer_count" fill={BRAND.gold} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>

            {/* 平均単価 */}
            <ChartCard title="平均単価（税抜） 推移">
              <ResponsiveContainer width="100%" height={240}>
                <LineChart data={series} margin={{ top: 10, right: 12, left: 0, bottom: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E5E5" />
                  <XAxis dataKey="period_label" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => formatCurrency(v)} />
                  <Tooltip formatter={(v: number) => formatCurrency(v)} />
                  <Line type="monotone" dataKey="avg_price_tax_excluded" stroke={BRAND.red} strokeWidth={2} dot={{ r: 3 }} />
                </LineChart>
              </ResponsiveContainer>
            </ChartCard>
          </div>

          {/* 各種率（マルチライン） */}
          <ChartCard title="各種KPI率 推移">
            <div className="flex items-center gap-2 flex-wrap mb-3">
              <span className="text-xs text-neutral-sub mr-1">表示メトリクス:</span>
              {RATE_METRICS.map((m) => {
                const on = enabledMetrics.has(m.key as string);
                return (
                  <button
                    key={m.key as string}
                    type="button"
                    onClick={() => toggleMetric(m.key as string)}
                    aria-pressed={on}
                    className={`inline-flex items-center gap-1 px-2 py-1 text-xs rounded border transition ${
                      on ? 'bg-white border-neutral-line' : 'bg-neutral-panel border-neutral-line text-neutral-sub opacity-60'
                    }`}
                  >
                    <span className="w-2 h-2 rounded-full" style={{ background: m.color }} />
                    {m.label}
                  </button>
                );
              })}
              <button
                type="button"
                onClick={() => setEnabledMetrics(new Set(RATE_METRICS.map((m) => m.key as string)))}
                className="ml-2 text-xs text-brand-red hover:underline"
              >
                全選択
              </button>
              <button
                type="button"
                onClick={() => setEnabledMetrics(new Set())}
                className="text-xs text-neutral-sub hover:text-brand-red"
              >
                全解除
              </button>
            </div>
            <ResponsiveContainer width="100%" height={320}>
              <LineChart data={series} margin={{ top: 10, right: 12, left: 0, bottom: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E5E5" />
                <XAxis dataKey="period_label" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => `${(v * 100).toFixed(0)}%`} domain={['auto', 'auto']} />
                <Tooltip formatter={(v: number, k: string) => [formatPercent(v), labelJa(k)]} />
                <Legend formatter={(v) => labelJa(v)} />
                {RATE_METRICS.filter((m) => enabledMetrics.has(m.key as string)).map((m) => (
                  <Line
                    key={m.key as string}
                    type="monotone"
                    dataKey={m.key as string}
                    stroke={m.color}
                    strokeWidth={2}
                    dot={{ r: 3 }}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </ChartCard>

          {/* 明細テーブル */}
          <div className="border border-neutral-line rounded-lg bg-white">
            <div className="px-4 py-3 border-b border-neutral-line bg-neutral-panel">
              <h2 className="text-h3">月次明細</h2>
            </div>
            <div className="overflow-x-auto">
            <table className="w-full min-w-[960px] text-xs">
              <thead className="bg-neutral-panel/50 text-neutral-sub">
                <tr>
                  <th className="text-left px-3 py-2 whitespace-nowrap">月</th>
                  <th className="text-right px-3 py-2 whitespace-nowrap">売上（税抜）</th>
                  <th className="text-right px-3 py-2 whitespace-nowrap">原価</th>
                  <th className="text-right px-3 py-2 whitespace-nowrap">人件費</th>
                  <th className="text-right px-3 py-2 whitespace-nowrap">諸経費</th>
                  <th className="text-right px-3 py-2 whitespace-nowrap">来客数</th>
                  <th className="text-right px-3 py-2 whitespace-nowrap">平均単価</th>
                  <th className="text-right px-3 py-2 whitespace-nowrap">原価率</th>
                  <th className="text-right px-3 py-2 whitespace-nowrap">粗利率</th>
                  <th className="text-right px-3 py-2 whitespace-nowrap">人件費率</th>
                  <th className="text-right px-3 py-2 whitespace-nowrap">諸経費率</th>
                  <th className="text-right px-3 py-2 whitespace-nowrap">営業利益率</th>
                </tr>
              </thead>
              <tbody>
                {series.map((r) => (
                  <tr key={r.period} className="border-t border-neutral-line hover:bg-neutral-panel">
                    <td className="px-3 py-1.5 tabular-nums whitespace-nowrap">{r.period_label}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums whitespace-nowrap">{formatCurrency(r.total_sales_tax_excluded)}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums whitespace-nowrap">{formatCurrency(r.cogs_tax_excluded)}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums whitespace-nowrap">{formatCurrency(r.labor_cost)}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums whitespace-nowrap">{formatCurrency(r.opex)}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums whitespace-nowrap">{formatCount(r.customer_count)}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums whitespace-nowrap">{formatCurrency(r.avg_price_tax_excluded)}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums whitespace-nowrap">{formatPercent(r.cost_rate)}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums whitespace-nowrap">{formatPercent(r.gross_margin_rate)}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums whitespace-nowrap">{formatPercent(r.labor_rate)}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums whitespace-nowrap">{formatPercent(r.opex_rate)}</td>
                    <td className="px-3 py-1.5 text-right tabular-nums whitespace-nowrap">{formatPercent(r.operating_profit_rate)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function SummaryTile({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-lg border border-neutral-line bg-white p-3">
      <div className="text-xs text-neutral-sub">{label}</div>
      <div className="text-h3 tabular-nums mt-1">{value}</div>
    </div>
  );
}

function ChartCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="border border-neutral-line rounded-lg bg-white overflow-hidden">
      <div className="px-4 py-3 border-b border-neutral-line bg-neutral-panel">
        <h2 className="text-h3">{title}</h2>
      </div>
      <div className="p-4">
        {children}
      </div>
    </div>
  );
}

const labelJa = (key: string): string => ({
  store_sales_tax_excluded: '店舗内売上',
  delivery_sales_tax_excluded: 'デリバリー売上',
  customer_count: '来客数',
  avg_price_tax_excluded: '平均単価',
  cost_rate: '原価率',
  gross_margin_rate: '粗利率',
  labor_rate: '人件費率',
  opex_rate: '諸経費率',
  operating_profit_rate: '営業利益率',
}[key] ?? key);
